<template>
  <div class="w-full">
    <div class="flex flex-col min-w-[16rem] w-full">
      <div class="relative focus-within:z-10">
        <s-input
          v-model="filterText"
          placeholder="Filter Options"
          class="w-full !rounded-b-none !border-b-0 pr-8"
          ref="searchInput"
        />
        <s-icon
          name="magnify"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 transition ease-out duration-100 mt-px"
          :class="[filterText ? 'opacity-0' : 'opacity-70']"
          aria-hidden="true"
        />
      </div>
      <ul class="max-h-60 overflow-auto border border-gray-200 scroll-shadow">
        <li
          v-if="filteredOptions.length === 0"
          class="py-2 px-4 text-left bg-gray-100 text-gray-500 whitespace-nowrap"
        >
          {{ $t('filter.noResults') }}
        </li>
        <li
          v-else
          v-for="option in filteredOptions"
          :key="option.value"
          class="-mb-px first:-mt-px"
        >
          <button
            class="w-full text-left transition-colors ease-out duration-100 py-1.5 px-3 border-y hover:border-blue-200/20 hover:bg-blue-200/20 cursor-pointer select-none"
            :class="[
              isSelected(option) ? 'bg-blue-200/20 border-blue-200/20' : 'border-transparent',
            ]"
            @click="selectOption(option)"
          >
            <slot name="option" v-bind="{option}">
              <span class="block truncate">{{ option.label }}</span>
            </slot>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends Option">
import {computed, ref, watch} from 'vue';
import SIcon from './SIcon.vue';
import SInput from './SInput.vue';
import type {Option} from './controls/Option';

const {disabled = false, options} = defineProps<{
  options: T[];
  disabled?: boolean;
}>();

const model = defineModel<T['value'] | null>();
const emit = defineEmits(['close']);

const filterText = ref('');
const selectedOption = ref<T | null>(null);
const searchInput = ref<HTMLElement | null>(null);

// Update selected option when modelValue changes
watch(
  model,
  (newValue) => {
    selectedOption.value = options.find((option) => option.value === newValue) || null;
  },
  {immediate: true}
);

// Filtered options based on search text
const filteredOptions = computed(() => {
  if (!filterText.value) return options;

  return options.filter((option) => {
    const label = option.label.toLowerCase();
    return label.includes(filterText.value.toLowerCase());
  });
});

function isSelected(option: T): boolean {
  return selectedOption.value && selectedOption.value.value === option.value;
}

// Select an option and close the dialog
function selectOption(option: T): void {
  if (disabled) return;

  selectedOption.value = option;
  model.value = option.value;
  emit('close');
}
</script>
